<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="600px" persistent scrollable>
        <v-card>
            <v-card-title class="headline primary white--text">
                <v-icon left color="white">mdi-file-edit</v-icon>
                Edit Resource
                <v-spacer></v-spacer>
                <v-btn icon color="white" @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-6">
                <v-form ref="editForm" v-model="formValid" lazy-validation>
                    <!-- Resource Type Display -->
                    <div class="mb-4">
                        <v-chip :color="resourceTypeColor" text-color="white" small class="resource-type-chip">
                            <v-icon left small>{{ resourceTypeIcon }}</v-icon>
                            {{ resourceTypeText }}
                        </v-chip>
                    </div>

                    <!-- Resource Name -->
                    <v-text-field v-model="formData.name" :rules="nameRules" label="Resource Name"
                        prepend-inner-icon="mdi-file-document" outlined dense class="mb-4" :disabled="updating" />

                    <!-- URL Field (for URL resources) -->
                    <v-text-field 
                        v-if="isUrlResource" 
                        v-model="formData.url" 
                        :rules="urlRules" 
                        label="Resource URL" 
                        prepend-inner-icon="mdi-link"
                        outlined 
                        dense 
                        class="mb-4" 
                        :disabled="updating"
                        hint="Update the URL for this resource"
                        persistent-hint />

                    <!-- Description -->
                    <v-textarea v-model="formData.description" label="Description" prepend-inner-icon="mdi-text"
                        outlined dense rows="3" class="mb-4" :disabled="updating" />

                    <!-- Category -->
                    <v-select v-model="formData.category" :items="categoryOptions" :rules="categoryRules"
                        label="Category" prepend-inner-icon="mdi-folder" outlined dense class="mb-4"
                        :disabled="updating" />

                    <!-- Company -->
                    <v-select v-model="formData.company_id" :items="companyOptions" item-text="text" item-value="value"
                        label="Company" prepend-inner-icon="mdi-domain" outlined dense clearable class="mb-4"
                        :disabled="updating" />

                    <!-- Internal Resource Toggle -->
                    <v-checkbox 
                        v-model="formData.is_internal_resource" 
                        label="Internal Resource Only" 
                        class="mb-4"
                        :disabled="updating"
                        hint="Mark as internal if this resource should only be accessible within the organization"
                        persistent-hint />

                    <!-- Tags -->
                    <v-combobox v-model="formData.tags" :items="availableTags" label="Tags"
                        prepend-inner-icon="mdi-tag-multiple" outlined dense multiple chips deletable-chips small-chips
                        class="mb-4" :disabled="updating" hint="Press Enter to add new tags" persistent-hint>
                        <template v-slot:selection="{ item, index }">
                            <v-chip small close @click:close="removeTag(index)" color="primary" text-color="white">
                                {{ item }}
                            </v-chip>
                        </template>
                    </v-combobox>

                    <!-- Current File Info -->
                    <v-card outlined class="mt-4 file-info-card" v-if="resource">
                        <v-card-text class="pa-4">
                            <div class="d-flex align-center">
                                <v-icon :color="getFileTypeColor(resource.file_type || '')" size="32" class="mr-3">
                                    {{ getFileTypeIcon(resource.file_type || '') }}
                                </v-icon>
                                <div class="flex-grow-1">
                                    <h4 class="text-subtitle-2 font-weight-medium">{{ resource.name || 'Untitled' }}
                                    </h4>
                                    <div class="d-flex align-center text-caption grey--text">
                                        <span>{{ resource.size || 'Unknown size' }}</span>
                                        <v-divider vertical class="mx-2" style="height: 12px;"></v-divider>
                                        <span>{{ resource.file_type?.toUpperCase() || 'UNKNOWN' }}</span>
                                        <v-divider vertical class="mx-2" style="height: 12px;"></v-divider>
                                        <span>{{ resource.created_at ? formatDate(resource.created_at) : 'Unknown date' }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>

                    <!-- Error Message -->
                    <v-alert v-if="errorMessage" type="error" dense class="mt-4" dismissible @input="errorMessage = ''">
                        {{ errorMessage }}
                    </v-alert>
                </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-6">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog" :disabled="updating">
                    Cancel
                </v-btn>
                <v-btn color="primary" @click="updateResource" :loading="updating" :disabled="!formValid || updating">
                    <v-icon left>mdi-content-save</v-icon>
                    Update Resource
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'EditResourceDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        resource: {
            type: Object,
            required: true,
            validator: function (value) {
                return value && typeof value === 'object' && value.id;
            }
        },
        companies: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            formValid: false,
            updating: false,
            errorMessage: '',
            formData: {
                name: '',
                description: '',
                category: '',
                company_id: null,
                tags: [],
                url: '',
                is_internal_resource: false
            },
            nameRules: [
                v => !!v || 'Resource name is required',
                v => (v && v.length <= 255) || 'Name must be less than 255 characters'
            ],
            urlRules: [
                v => !this.isUrlResource || !!v || 'URL is required for URL resources',
                v => !this.isUrlResource || !v || this.isValidUrl(v) || 'Please enter a valid URL (must start with http:// or https://)'
            ],
            categoryRules: [
                v => !!v || 'Category is required'
            ],
            categoryOptions: [
                'SOP',
                'Template',
                'EOD',
                'SOD',
                'Guide',
                'Other'
            ],
            availableTags: [
                'Important',
                'Urgent',
                'Training',
                'Policy',
                'Procedure',
                'Reference',
                'Documentation',
                'Guidelines'
            ]
        };
    },
    computed: {
        companyOptions() {
            const options = [
                { text: 'Shared Resource', value: null }
            ];

            if (this.companies && this.companies.length > 0) {
                this.companies.forEach(company => {
                    options.push({
                        text: company.name,
                        value: company.id
                    });
                });
            }

            return options;
        },
        isUrlResource() {
            return this.resource && (this.resource.type === 'url' || (this.resource.url && !this.resource.file_path));
        },
        resourceTypeText() {
            return this.isUrlResource ? 'URL Resource' : 'File Resource';
        },
        resourceTypeIcon() {
            return this.isUrlResource ? 'mdi-link' : 'mdi-file';
        },
        resourceTypeColor() {
            return this.isUrlResource ? 'blue' : 'green';
        }
    },
    watch: {
        value(newVal) {
            if (newVal && this.resource) {
                this.initializeForm();
            }
        },
        resource: {
            handler(newResource) {
                if (newResource && this.value) {
                    this.initializeForm();
                }
            },
            immediate: true
        }
    },
    methods: {
        initializeForm() {
            if (!this.resource) {
                console.warn('EditResourceDialog: No resource provided');
                return;
            }

            // Initialize form with current resource data
            this.formData = {
                name: this.resource.name || '',
                description: this.resource.description || '',
                category: this.resource.category || '',
                company_id: this.resource.company_id || null,
                tags: this.normalizeResourceTags(),
                url: this.resource.url || '',
                is_internal_resource: this.resource.is_internal_resource || false
            };

            // Reset form validation
            this.$nextTick(() => {
                if (this.$refs.editForm) {
                    this.$refs.editForm.resetValidation();
                }
            });
        },

        normalizeResourceTags() {
            if (!this.resource.tags) return [];

            // If tags is already an array
            if (Array.isArray(this.resource.tags)) {
                // Check if it's an array of tag objects with a 'tag' property
                if (this.resource.tags.length > 0 && typeof this.resource.tags[0] === 'object' && this.resource.tags[0].tag) {
                    return this.resource.tags.map(tagObj => tagObj.tag);
                }
                // If it's an array of strings
                return [...this.resource.tags];
            }

            // If tags is a string, try to parse it
            if (typeof this.resource.tags === 'string') {
                try {
                    // Try parsing as JSON first
                    const parsed = JSON.parse(this.resource.tags);
                    if (Array.isArray(parsed)) {
                        // Check if parsed array contains objects with 'tag' property
                        if (parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0].tag) {
                            return parsed.map(tagObj => tagObj.tag);
                        }
                        return parsed;
                    }
                } catch (e) {
                    // If JSON parse fails, split by comma and clean up
                    return this.resource.tags
                        .split(',')
                        .map(tag => tag.trim())
                        .filter(tag => tag.length > 0);
                }
            }

            return [];
        },

        removeTag(index) {
            this.formData.tags.splice(index, 1);
        },

        async updateResource() {
            // Validate form first
            if (!this.$refs.editForm || !this.$refs.editForm.validate()) {
                this.errorMessage = 'Please fill in all required fields correctly.';
                return;
            }

            // Check if resource is available
            if (!this.resource || !this.resource.id) {
                this.errorMessage = 'Resource information is missing. Please close and try again.';
                return;
            }

            this.updating = true;
            this.errorMessage = '';

            try {
                // Prepare update data
                const updateData = {
                    name: this.formData.name.trim(),
                    description: this.formData.description ? this.formData.description.trim() : null,
                    category: this.formData.category,
                    company_id: this.formData.company_id || null,
                    tags: this.formData.tags && this.formData.tags.length > 0 ? this.formData.tags : [],
                    is_internal_resource: this.formData.is_internal_resource === true
                };

                // Add URL if this is a URL resource
                if (this.isUrlResource) {
                    updateData.url = this.formData.url.trim();
                }

                // Emit update event to parent component
                this.$emit('update-resource', {
                    resourceId: this.resource.id,
                    updateData: updateData
                });

            } catch (error) {
                console.error('Error in updateResource:', error);
                this.errorMessage = error.message || 'Failed to update resource. Please try again.';
            } finally {
                this.updating = false;
            }
        },

        closeDialog() {
            if (!this.updating) {
                this.$emit('input', false);
                this.resetForm();
            }
        },

        resetForm() {
            this.formData = {
                name: '',
                description: '',
                category: '',
                company_id: null,
                tags: [],
                url: '',
                is_internal_resource: false
            };
            this.errorMessage = '';

            if (this.$refs.editForm) {
                this.$refs.editForm.resetValidation();
            }
        },

        isValidUrl(url) {
            try {
                new URL(url);
                return url.startsWith('http://') || url.startsWith('https://');
            } catch {
                return false;
            }
        },

        // File type utilities (reused from ResourceCard)
        getFileTypeIcon(fileType) {
            const iconMap = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word-box',
                docx: 'mdi-file-word-box',
                xls: 'mdi-file-excel-box',
                xlsx: 'mdi-file-excel-box',
                ppt: 'mdi-file-powerpoint-box',
                pptx: 'mdi-file-powerpoint-box',
                txt: 'mdi-file-document-outline',
                zip: 'mdi-folder-zip',
                image: 'mdi-file-image-box',
                video: 'mdi-file-video-box',
                audio: 'mdi-file-music-box'
            };
            return iconMap[fileType] || 'mdi-file-document-outline';
        },

        getFileTypeColor(fileType) {
            const colorMap = {
                pdf: 'red',
                doc: 'blue',
                docx: 'blue',
                xls: 'green',
                xlsx: 'green',
                ppt: 'orange',
                pptx: 'orange',
                txt: 'grey',
                zip: 'purple',
                image: 'pink',
                video: 'deep-purple',
                audio: 'teal'
            };
            return colorMap[fileType] || 'grey';
        },

        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        }
    }
};
</script>

<style scoped>
.file-info-card {
    background-color: #f8f9fa;
    border: 1px dashed #dee2e6;
}

.tag-chip {
    margin: 2px;
}

/* Resource type chip */
.resource-type-chip {
    font-weight: 600;
    font-size: 12px;
}

/* Form styling */
.v-text-field--outlined>>>.v-input__slot {
    border-radius: 8px;
}

.v-textarea--outlined>>>.v-input__slot {
    border-radius: 8px;
}

.v-select--outlined>>>.v-input__slot {
    border-radius: 8px;
}
</style>